"use client";

import { Bot, Sparkles } from "lucide-react";
import { useAgentStore } from "@/lib/stores/useAgentStore";
import { cn } from "@/lib/utils";

/**
 * AgentFAB - Floating Action Button to toggle the Agent Sidebar
 *
 * Features:
 * - Fixed position at bottom-right
 * - Animated icon when agent is processing
 * - Badge for unread messages (future enhancement)
 * - Responsive positioning for mobile
 */
export function AgentFAB() {
  const { toggleSidebar, isSidebarOpen, isLoading, isConnected } = useAgentStore();

  return (
    <button
      onClick={toggleSidebar}
      className={cn(
        // Base styles
        "fixed z-50 flex items-center justify-center",
        "w-14 h-14 rounded-full shadow-lg",
        "transition-all duration-300 ease-in-out",
        "focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900",

        // Positioning - bottom right, with safe area padding on mobile
        "bottom-6 right-6",
        "sm:bottom-8 sm:right-8",

        // Gradient background
        "bg-gradient-to-br from-purple-600 to-pink-600",
        "hover:from-purple-500 hover:to-pink-500",
        "active:from-purple-700 active:to-pink-700",

        // When sidebar is open, slightly dim the button
        isSidebarOpen && "opacity-50 hover:opacity-100",

        // Loading state animation
        isLoading && "animate-pulse"
      )}
      aria-label={isSidebarOpen ? "Close Kreayt Agent" : "Open Kreayt Agent"}
      title={isSidebarOpen ? "Close Kreayt Agent" : "Open Kreayt Agent"}
      data-testid="agent-fab"
    >
      {/* Icon with animation */}
      <div className="relative">
        <Bot
          className={cn(
            "w-7 h-7 text-white transition-transform duration-300",
            isLoading && "animate-bounce"
          )}
        />

        {/* Processing indicator sparkles */}
        {isLoading && (
          <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-yellow-300 animate-pulse" />
        )}

        {/* Connection status dot */}
        <div
          className={cn(
            "absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-gray-900",
            isConnected ? "bg-green-500" : "bg-yellow-500"
          )}
        />
      </div>
    </button>
  );
}
